<script setup>
// NOTICE: KAPTION-INTEGRATION
// two-step-verification.UI.4 — the one "Two-step verification" section in
// profile settings (canvas: Main). Lists every method; a person may have
// several. Mounted by Index.vue only when useKaptionTwoStep().isEnabled.
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import FormSection from 'dashboard/components/FormSection.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import KaptionTwoStepAPI from 'dashboard/api/kaptionTwoStep';
import KaptionTwoStepSetup from 'dashboard/components/kaption/KaptionTwoStep/KaptionTwoStepSetup.vue';
import { useKaptionTwoStep } from 'dashboard/composables/useKaptionTwoStep';

const { t, locale } = useI18n();
const {
  status,
  fetchStatus,
  setStatus,
  isActive,
  isRequired,
  needsSetup,
  configuredMethods,
  requiredAccountName,
  passkeyAvailable,
  passkeyElsewhereHost,
} = useKaptionTwoStep();

const setupRef = ref(null);
const busyPreferred = ref(false);

onMounted(() => fetchStatus({ force: true }));

const intlLocale = computed(() =>
  String(locale.value || 'en').replace('_', '-')
);
const formatDate = iso =>
  iso
    ? new Intl.DateTimeFormat(intlLocale.value, {
        day: 'numeric',
        month: 'long',
      }).format(new Date(iso))
    : '';

const methods = computed(() => status.value?.methods || {});
const passkeys = computed(() => methods.value.passkey?.passkeys || []);
const preferred = computed(() => status.value?.preferred_method);
const activeCount = computed(() => configuredMethods.value.length);

const notice = computed(() => {
  const account = requiredAccountName.value;
  const count = activeCount.value;
  if (isRequired.value && needsSetup.value)
    return {
      tone: 'amber',
      text: t('KAPTION_TWO_STEP.NOTICE.REQUIRED_PENDING', { account }),
    };
  if (isRequired.value)
    return {
      tone: 'teal',
      text:
        count === 1
          ? t('KAPTION_TWO_STEP.NOTICE.REQUIRED_OK_ONE', { account })
          : t('KAPTION_TWO_STEP.NOTICE.REQUIRED_OK', { account, count }),
    };
  if (count > 0)
    return {
      tone: 'teal',
      text:
        count === 1
          ? t('KAPTION_TWO_STEP.NOTICE.OPTIONAL_OK_ONE')
          : t('KAPTION_TWO_STEP.NOTICE.OPTIONAL_OK', { count }),
    };
  return { tone: 'slate', text: t('KAPTION_TWO_STEP.NOTICE.OPTIONAL_NONE') };
});

const NOTICE_CLASSES = {
  amber: 'bg-n-amber-3 text-n-amber-11',
  teal: 'bg-n-teal-3 text-n-teal-11',
  slate: 'bg-n-alpha-black2 text-n-slate-11',
};

const passkeyDescription = () => {
  const list = passkeys.value;
  // two-step-verification.PASSKEY.3 — this host is outside the passkey RP ID.
  if (passkeyElsewhereHost.value)
    return t('KAPTION_TWO_STEP.METHODS.PASSKEY.OTHER_HOST', {
      host: passkeyElsewhereHost.value,
    });
  if (!list.length) return t('KAPTION_TWO_STEP.METHODS.PASSKEY.DESC');
  const latest = list[list.length - 1];
  if (list.length === 1)
    return t('KAPTION_TWO_STEP.METHODS.PASSKEY.ADDED', {
      name:
        latest.nickname || t('KAPTION_TWO_STEP.PASSKEY_DIALOG.NAME_DEFAULT'),
      date: formatDate(latest.created_at),
    });
  return t('KAPTION_TWO_STEP.METHODS.PASSKEY.MANY', {
    count: list.length,
    date: formatDate(latest.created_at),
  });
};

// No "add" on a host where the browser cannot create one; removing still works.
const passkeyCta = on => {
  if (!passkeyAvailable.value) return '';
  return on
    ? t('KAPTION_TWO_STEP.METHODS.PASSKEY.ADD_ANOTHER')
    : t('KAPTION_TWO_STEP.ACTIONS.ADD');
};

const rows = computed(() => {
  const m = methods.value;
  const nothingYet = activeCount.value === 0;
  const list = [];

  if (passkeyAvailable.value || m.passkey?.configured) {
    const on = !!m.passkey?.configured;
    list.push({
      key: 'passkey',
      icon: 'i-lucide-key-round',
      title: t('KAPTION_TWO_STEP.METHODS.PASSKEY.TITLE'),
      on,
      badge: on
        ? t('KAPTION_TWO_STEP.BADGE.ON')
        : t('KAPTION_TWO_STEP.BADGE.OFF'),
      description: passkeyDescription(),
      cta: passkeyCta(on),
      solid: !on && nothingYet,
      ctaDisabled: !passkeyAvailable.value,
      onCta: () => setupRef.value?.add('passkey'),
      canRemove: on && passkeys.value.length === 1,
      onRemove: () => setupRef.value?.remove('passkey', passkeys.value[0]?.id),
    });
  }

  const waOn = !!m.whatsapp?.configured;
  list.push({
    key: 'whatsapp',
    icon: 'i-lucide-message-circle',
    title: t('KAPTION_TWO_STEP.METHODS.WHATSAPP.TITLE'),
    on: waOn,
    badge: waOn
      ? t('KAPTION_TWO_STEP.BADGE.ON')
      : t('KAPTION_TWO_STEP.BADGE.OFF'),
    description: waOn
      ? t('KAPTION_TWO_STEP.METHODS.WHATSAPP.CONFIGURED', {
          phone: m.whatsapp?.phone,
        })
      : t('KAPTION_TWO_STEP.METHODS.WHATSAPP.DESC'),
    cta: waOn
      ? t('KAPTION_TWO_STEP.METHODS.WHATSAPP.CHANGE')
      : t('KAPTION_TWO_STEP.ACTIONS.ADD'),
    solid: false,
    onCta: () => setupRef.value?.add('whatsapp'),
    canRemove: waOn,
    onRemove: () => setupRef.value?.remove('whatsapp'),
  });

  // Hidden (not an error) when the installation has no encryption keys.
  if (m.authenticator?.available) {
    const on = !!m.authenticator?.configured;
    list.push({
      key: 'authenticator',
      icon: 'i-lucide-smartphone',
      title: t('KAPTION_TWO_STEP.METHODS.AUTHENTICATOR.TITLE'),
      on,
      badge: on
        ? t('KAPTION_TWO_STEP.BADGE.ON')
        : t('KAPTION_TWO_STEP.BADGE.OFF'),
      description: on
        ? t('KAPTION_TWO_STEP.METHODS.AUTHENTICATOR.CONFIGURED')
        : t('KAPTION_TWO_STEP.METHODS.AUTHENTICATOR.DESC'),
      cta: on ? '' : t('KAPTION_TWO_STEP.ACTIONS.ADD'),
      solid: false,
      onCta: () => setupRef.value?.add('authenticator'),
      canRemove: on,
      onRemove: () => setupRef.value?.remove('authenticator'),
    });
  }

  const codes = m.backup_codes || {};
  const hasCodes = codes.remaining > 0;
  list.push({
    key: 'backup_codes',
    icon: 'i-lucide-list-ordered',
    title: t('KAPTION_TWO_STEP.METHODS.BACKUP.TITLE'),
    on: hasCodes,
    badge: hasCodes
      ? t('KAPTION_TWO_STEP.BADGE.CODES_LEFT', {
          remaining: codes.remaining,
          total: codes.total || 10,
        })
      : t('KAPTION_TWO_STEP.BADGE.CODES_PENDING'),
    description: hasCodes
      ? t('KAPTION_TWO_STEP.METHODS.BACKUP.CONFIGURED')
      : t('KAPTION_TWO_STEP.METHODS.BACKUP.DESC'),
    cta: hasCodes
      ? t('KAPTION_TWO_STEP.METHODS.BACKUP.GENERATE')
      : t('KAPTION_TWO_STEP.METHODS.BACKUP.VIEW'),
    solid: false,
    ctaDisabled: !hasCodes && nothingYet,
    onCta: () =>
      hasCodes
        ? setupRef.value?.regenerateBackupCodes()
        : setupRef.value?.generateFirstBackupCodes(),
    canRemove: false,
  });

  return list;
});

const canMakePreferred = row =>
  row.on &&
  row.key !== 'backup_codes' &&
  activeCount.value > 1 &&
  preferred.value !== row.key;

const makePreferred = async method => {
  busyPreferred.value = true;
  try {
    const { data } = await KaptionTwoStepAPI.setPreferred(method);
    setStatus(data);
  } catch (e) {
    useAlert(e?.response?.data?.error || t('KAPTION_TWO_STEP.ERRORS.GENERIC'));
  } finally {
    busyPreferred.value = false;
  }
};

const removePasskey = id => setupRef.value?.remove('passkey', id);
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <FormSection
    v-if="isActive"
    :title="$t('KAPTION_TWO_STEP.SECTION_TITLE')"
    :description="$t('KAPTION_TWO_STEP.SECTION_NOTE')"
  >
    <div class="flex flex-col gap-6" data-testid="kpt-two-step-section">
      <div
        class="flex items-center gap-2.5 px-3 py-2.5 text-sm rounded-lg"
        :class="NOTICE_CLASSES[notice.tone]"
        data-testid="kpt-two-step-notice"
      >
        <Icon icon="i-lucide-shield" class="flex-shrink-0 size-4" />
        <span>{{ notice.text }}</span>
      </div>

      <div class="border rounded-xl border-n-weak bg-n-background">
        <div
          v-for="(row, index) in rows"
          :key="row.key"
          class="flex flex-col gap-4 p-4 sm:flex-row sm:items-center"
          :class="{ 'border-t border-n-weak': index > 0 }"
          :data-testid="`kpt-two-step-row-${row.key}`"
        >
          <div class="flex flex-col flex-1 min-w-0 gap-1.5">
            <div class="flex flex-wrap items-center gap-2">
              <Icon
                :icon="row.icon"
                class="flex-shrink-0 size-4 text-n-slate-10"
              />
              <h5 class="text-sm font-semibold text-n-slate-12">
                {{ row.title }}
              </h5>
              <span
                class="px-1.5 py-0.5 text-xs rounded-full"
                :class="
                  row.on
                    ? 'bg-n-teal-3 text-n-teal-11'
                    : 'bg-n-alpha-black2 text-n-slate-11'
                "
              >
                {{ row.badge }}
              </span>
              <span
                v-if="row.on && preferred === row.key"
                class="px-1.5 py-0.5 text-xs rounded-full bg-n-brand/10 text-n-brand"
              >
                {{ $t('KAPTION_TWO_STEP.BADGE.PREFERRED') }}
              </span>
            </div>
            <p class="text-sm text-n-slate-11">{{ row.description }}</p>
            <ul
              v-if="row.key === 'passkey' && passkeys.length > 1"
              class="flex flex-col gap-1 mt-1"
            >
              <li
                v-for="key in passkeys"
                :key="key.id"
                class="flex items-center gap-2 text-sm text-n-slate-11"
              >
                <Icon icon="i-lucide-key-round" class="size-3.5" />
                <span class="flex-1 truncate">
                  {{
                    $t('KAPTION_TWO_STEP.METHODS.PASSKEY.ADDED', {
                      name:
                        key.nickname ||
                        $t('KAPTION_TWO_STEP.PASSKEY_DIALOG.NAME_DEFAULT'),
                      date: formatDate(key.created_at),
                    })
                  }}
                </span>
                <Button
                  type="button"
                  ruby
                  link
                  xs
                  :label="$t('KAPTION_TWO_STEP.ACTIONS.REMOVE')"
                  @click="removePasskey(key.id)"
                />
              </li>
            </ul>
          </div>
          <div class="flex items-center flex-shrink-0 gap-2">
            <Button
              v-if="canMakePreferred(row)"
              type="button"
              slate
              ghost
              sm
              :disabled="busyPreferred"
              :label="$t('KAPTION_TWO_STEP.ACTIONS.MAKE_PREFERRED')"
              @click="makePreferred(row.key)"
            />
            <Button
              v-if="row.canRemove"
              type="button"
              ruby
              ghost
              sm
              :label="$t('KAPTION_TWO_STEP.ACTIONS.REMOVE')"
              :data-testid="`kpt-two-step-remove-${row.key}`"
              @click="row.onRemove"
            />
            <Button
              v-if="row.cta"
              type="button"
              sm
              :faded="!row.solid"
              :slate="row.key === 'backup_codes' && row.ctaDisabled"
              :disabled="row.ctaDisabled"
              :label="row.cta"
              :data-testid="`kpt-two-step-cta-${row.key}`"
              @click="row.onCta"
            />
          </div>
        </div>
      </div>
      <p class="text-[13px] text-n-slate-11">
        {{ $t('KAPTION_TWO_STEP.FOOTNOTE') }}
      </p>
    </div>
    <KaptionTwoStepSetup ref="setupRef" @updated="setStatus" />
  </FormSection>
</template>
